import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { ref } from 'vue';
import {
  databaseService,
  initializeDatabase,
  type OfflineEntityTable,
  type SyncStatus,
} from '@/services/database/DatabaseService';
import { isOfflineEntityTable } from '@/services/database/migrations';
import {
  MobileSyncHttpError,
  postMobileSync,
  type MobileSyncRecordPayload,
  type MobileSyncResultItem,
} from '@/services/mobileSyncApi';

const BATCH_SIZE = 50;
const MAX_ATTEMPTS = 3;
const META_LAST_SYNC = 'last_sync_at';

export const isSyncing = ref(false);
export const pendingCount = ref(0);
export const lastSyncAt = ref<string | null>(null);
export const lastSyncError = ref<string | null>(null);
export const isOnline = ref(true);

let watcherStarted = false;
let networkHandle: { remove: () => void } | undefined;
let initPromise: Promise<void> | null = null;

function log(message: string, extra?: unknown): void {
  if (import.meta.env.DEV) {
    console.info(`[SyncService] ${message}`, extra ?? '');
  }
}

async function refreshPendingCount(): Promise<void> {
  pendingCount.value = await databaseService.countPendingQueue();
}

export async function refreshOnlineStatus(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    isOnline.value = navigator.onLine;
    return;
  }

  const status = await Network.getStatus();
  isOnline.value = status.connected;
}

function queueItemToSyncRecord(item: {
  table_name: string;
  record_id: string;
  operation: string;
  payload: Record<string, unknown>;
  created_at: string;
}): MobileSyncRecordPayload | null {
  if (!isOfflineEntityTable(item.table_name)) {
    return null;
  }

  return {
    id: item.record_id,
    table: item.table_name,
    operation: item.operation as MobileSyncRecordPayload['operation'],
    data: item.payload,
    updated_at:
      typeof item.payload.updated_at === 'string'
        ? item.payload.updated_at
        : item.created_at,
  };
}

async function applyResultToLocal(
  table: OfflineEntityTable,
  recordId: string,
  result: MobileSyncResultItem,
): Promise<void> {
  let status: SyncStatus = 'pending';

  if (result.status === 'synced') {
    status = 'synced';
  } else if (result.status === 'conflict') {
    status = 'conflict';
  } else {
    status = 'error';
  }

  await databaseService.setSyncStatus(table, recordId, status);

  if (result.status === 'synced' && result.server_data) {
    const existing = await databaseService.findById(table, recordId, {
      includeDeleted: true,
    });

    if (existing) {
      await databaseService.update(
        table,
        recordId,
        {
          ...existing.data,
          ...result.server_data,
          serverId: result.server_data.serverId ?? result.server_data.server_id,
        },
        { markPending: false },
      );

      if (table === 'field_reports' && result.server_data.serverId) {
        await databaseService.runQuery(
          `UPDATE field_reports SET server_id = ?, version = ? WHERE id = ?`,
          [
            Number(result.server_data.serverId),
            Number(result.server_data.version ?? existing.version ?? 0),
            recordId,
          ],
        );
      }
    }
  }
}

export async function ensureOfflineDatabase(): Promise<void> {
  if (!initPromise) {
    initPromise = initializeDatabase();
  }

  await initPromise;
}

export async function syncPendingRecords(): Promise<boolean> {
  if (isSyncing.value) {
    return false;
  }

  await ensureOfflineDatabase();
  await refreshOnlineStatus();

  if (!isOnline.value) {
    log('Sem rede — sync adiado.');
    await refreshPendingCount();
    return false;
  }

  const queue = await databaseService.listPendingQueue(BATCH_SIZE);
  if (queue.length === 0) {
    await refreshPendingCount();
    return true;
  }

  isSyncing.value = true;
  lastSyncError.value = null;

  try {
    const records: MobileSyncRecordPayload[] = [];

    for (const item of queue) {
      const record = queueItemToSyncRecord(item);
      if (record) {
        records.push(record);
      }
    }

    if (records.length === 0) {
      return true;
    }

    log(`Enviando lote (${records.length})`, records);

    const response = await postMobileSync(records);
    const resultsById = new Map(response.results.map((r) => [r.id, r]));

    for (const item of queue) {
      const result = resultsById.get(item.record_id);

      if (!result) {
        await databaseService.markQueueAttempt(item.id, 'Resposta sem status para o registro.');
        continue;
      }

      if (isOfflineEntityTable(item.table_name)) {
        await applyResultToLocal(item.table_name, item.record_id, result);
      }

      if (result.status === 'synced') {
        await databaseService.removeQueueItem(item.id);

        if (item.table_name === 'field_reports') {
          const report = result.server_data?.report as
            | Record<string, unknown>
            | undefined;

          if (report && typeof report === 'object') {
            void import('@/mobile/sync/fieldSyncCache').then(
              ({ reconcileFieldOutboxWithServerReports }) =>
                reconcileFieldOutboxWithServerReports([
                  report as import('@/modules/field/types').FieldReportListItem,
                ]),
            );
          } else {
            void import('@/mobile/sync/fieldSyncApi').then(({ fetchFieldBootstrap }) =>
              import('@/mobile/sync/fieldSyncCache').then(
                async ({ reconcileFieldOutboxWithServerReports, mergeFieldCacheDelta }) => {
                  const bootstrap = await fetchFieldBootstrap();
                  await mergeFieldCacheDelta(bootstrap);
                  await reconcileFieldOutboxWithServerReports(bootstrap.reports);
                },
              ),
            );
          }
        }
      } else if (result.status === 'conflict') {
        await databaseService.removeQueueItem(item.id);
        log('Conflito', { id: item.record_id, result });
      } else {
        await databaseService.markQueueAttempt(
          item.id,
          result.message ?? 'Erro ao sincronizar.',
        );

        const refreshed = await databaseService.listPendingQueue(1);
        const pendingItem = refreshed.find((q) => q.id === item.id);
        if (pendingItem && pendingItem.attempts >= MAX_ATTEMPTS && isOfflineEntityTable(item.table_name)) {
          await databaseService.setSyncStatus(item.table_name, item.record_id, 'error');
        }
      }
    }

    const syncedAt = new Date().toISOString();
    await databaseService.setMeta(META_LAST_SYNC, syncedAt);
    lastSyncAt.value = syncedAt;

    return true;
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Falha na sincronização offline.';

    lastSyncError.value = message;
    log('Erro no sync', error);

    if (error instanceof MobileSyncHttpError && error.status === 409) {
      for (const item of queue) {
        if (isOfflineEntityTable(item.table_name)) {
          await databaseService.setSyncStatus(item.table_name, item.record_id, 'conflict');
        }
        await databaseService.removeQueueItem(item.id);
      }
    } else {
      for (const item of queue) {
        await databaseService.markQueueAttempt(
          item.id,
          message.slice(0, 500),
        );
      }
    }

    return false;
  } finally {
    isSyncing.value = false;
    await refreshPendingCount();
  }
}

export async function loadSyncMeta(): Promise<void> {
  await ensureOfflineDatabase();
  lastSyncAt.value = await databaseService.getMeta(META_LAST_SYNC);
  await refreshPendingCount();
}

export function startSyncNetworkWatcher(): void {
  if (watcherStarted) {
    return;
  }

  watcherStarted = true;

  if (!Capacitor.isNativePlatform()) {
    const onOnline = () => {
      isOnline.value = true;
      void syncPendingRecords();
    };

    window.addEventListener('online', onOnline);
    networkHandle = {
      remove: () => window.removeEventListener('online', onOnline),
    };

    return;
  }

  void Network.addListener('networkStatusChange', (status) => {
    isOnline.value = status.connected;
    if (status.connected) {
      void syncPendingRecords();
      void import('@/mobile/offline/prefetchFieldPages').then(({ prefetchFieldInertiaPages }) =>
        prefetchFieldInertiaPages(),
      );
    }
  }).then((handle) => {
    networkHandle = handle;
  });
}

export function stopSyncNetworkWatcher(): void {
  networkHandle?.remove();
  networkHandle = undefined;
  watcherStarted = false;
}

export function useSyncService() {
  return {
    isSyncing,
    pendingCount,
    lastSyncAt,
    lastSyncError,
    isOnline,
    syncNow: syncPendingRecords,
    startWatcher: startSyncNetworkWatcher,
    stopWatcher: stopSyncNetworkWatcher,
    ensureDatabase: ensureOfflineDatabase,
    loadMeta: loadSyncMeta,
  };
}
